import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// Mini Cooper (classic Mk1, ~1965) — the definitive tiny British city car at
// true scale (1 unit = 1 metre): length ~2.9-3.05 (TINY), width 1.41, height
// 1.35 (tall and stubby — a short, upright box). Wheelbase ~2.0 on tiny 10-inch
// wheels pushed to the extreme corners with almost no overhang — the Mini's
// signature "a wheel at each corner". Nose is +Z. A short upright two-box: a
// short bonnet, a tall upright greenhouse whose glass fills the cabin, and a
// short tail. The iconic two-tone paint — body-colour lower body with a
// contrasting WHITE roof that sits flush on top of the cabin — plus round
// headlamps in the wings, a bright chrome oval grille with horizontal bars,
// chrome overrider bumpers, external door hinges, tiny steel wheels with chrome
// hubcaps, small round tail lamps and a twin exhaust. Body panels carry the
// "paint" recolour slot (iconic Mini red); the roof stays white (unslotted).
// `drive` spins the wheels and `hazards` blinks the four indicators.

const PAINT = "#b5222a"; // iconic Mini red — the "paint" slot default
const ROOF = "#eceeec"; // contrasting white roof — the two-tone signature (unslotted)
const TRIM = "#17181b"; // gloss-black trim (grille inset, cowl, seams)
const WELL = "#141518"; // wheel wells
const GLASS = "#2a3742"; // side and rear glazing
const SCREEN = "#3a4c5e"; // windscreen (a shade lighter)
const CHROME = "#ccd1d6"; // fine bright trim, window surrounds
const BUMPER = "#d3d8dc"; // brighter chrome — bumpers, grille bars, hubcaps
const STEEL = "#aeb4bb"; // steel wheel disc
const TIRE = "#1a1b1d";
const BRAKE = "#8c9196";
const LAMP = "#e8f0f4"; // headlamp glass
const AMBER = "#e8a13a";
const RED = "#a91527"; // tail lamps
const PLATE = "#eceff2";
const DARK = "#101113"; // grille void, headlamp bucket

// Spec-sheet skeleton (metres). Wheels jammed into the corners: tiny overhangs,
// tyre faces proud of the flanks. A short tub with a tall glassy cabin above.
const WHEEL_R = 0.19; // 10-inch wheels
const TYRE_W = 0.14; // narrow tyres
const WHEEL_X = 0.64; // half track — tyre face proud of the body flank
const AXLE_F = 1.0;
const AXLE_R = -1.0; // wheelbase 2.0
const BODY_HALF = 0.66; // body half-width (width ~1.32)
const ROOF_HALF = 0.605; // roof half-width — just over the glass, inset from the body
const WAIST = 0.8; // belt line — bonnet top / cabin floor (low, for a tall glass band)
const FLOOR = 0.16; // bottom of the body tub
const NOSE = 1.25; // tub front / rear face (|z|)

function box(
  w: number, h: number, d: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.BoxGeometry(w, h, d), color, position, rotation, ...extra };
}

function cyl(
  rTop: number, rBot: number, h: number, seg: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.CylinderGeometry(rTop, rBot, h, seg), color, position, rotation, ...extra };
}

/** A painted body panel — carries the "paint" recolour slot. */
function paint(w: number, h: number, d: number, position: Vec3, rotation?: Vec3): Part {
  return box(w, h, d, PAINT, position, rotation, { slot: "paint" });
}

/** A painted cylinder (rounded corner / edge) — carries the "paint" slot. */
function paintCyl(r: number, h: number, position: Vec3, rotation?: Vec3): Part {
  return cyl(r, r, h, 12, PAINT, position, rotation, { slot: "paint" });
}

/** One tiny 10-inch steel wheel: narrow tyre, brake disc, bright steel disc,
 *  chrome hubcap and a small chrome centre nub. Every part shares `name` so the
 *  `drive` track spins them as one node. `side` is +1 for the right (+x) wheel. */
function wheel(side: 1 | -1, z: number, name: string): Part[] {
  const x = side * WHEEL_X;
  const y = WHEEL_R;
  return [
    cyl(WHEEL_R, WHEEL_R, TYRE_W, 18, TIRE, [x, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.1, 0.1, 0.03, 12, BRAKE, [x - side * 0.05, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.15, 0.15, 0.04, 18, STEEL, [x + side * 0.04, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.11, 0.11, 0.03, 18, BUMPER, [x + side * 0.06, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.035, 0.035, 0.02, 10, CHROME, [x + side * 0.075, y, z], [0, 0, Math.PI / 2], { name }),
  ];
}

/** The wheel arch: a dark well disc flush with the body flank (behind the
 *  prouder tyre face) plus a rounded body-colour eyebrow — an upper half-torus
 *  over the tyre. The [0, pi/2, 0] yaw puts the 0..pi arc over the top half. */
function arch(side: 1 | -1, z: number): Part[] {
  return [
    cyl(0.28, 0.28, 0.03, 18, WELL, [side * (BODY_HALF - 0.005), WHEEL_R, z], [0, 0, Math.PI / 2]),
    {
      geometry: new THREE.TorusGeometry(0.27, 0.045, 6, 18, Math.PI),
      color: PAINT,
      position: [side * (BODY_HALF - 0.02), WHEEL_R, z] as Vec3,
      rotation: [0, Math.PI / 2, 0] as Vec3,
      slot: "paint",
    },
  ];
}

// The four hazard indicators — parts and blink tracks are generated from one
// table so the pivots (each part's own centre) can never drift from the parts.
// Front pair sits low in the wings beside the grille; rear pair sits beside the
// tail lamps.
const INDICATORS: ReadonlyArray<{ name: string; pos: Vec3; size: Vec3 }> = [
  { name: "indicator-fl", pos: [-0.5, 0.46, 1.265], size: [0.08, 0.06, 0.04] },
  { name: "indicator-fr", pos: [0.5, 0.46, 1.265], size: [0.08, 0.06, 0.04] },
  { name: "indicator-rl", pos: [-0.5, 0.58, -1.265], size: [0.07, 0.07, 0.04] },
  { name: "indicator-rr", pos: [0.5, 0.58, -1.265], size: [0.07, 0.07, 0.04] },
];

function parts(): Part[] {
  const P: Part[] = [];
  const S = [1, -1] as const;

  // -- Running gear ----------------------------------------------------------
  P.push(
    ...wheel(-1, AXLE_F, "wheel-fl"), ...wheel(1, AXLE_F, "wheel-fr"),
    ...wheel(-1, AXLE_R, "wheel-rl"), ...wheel(1, AXLE_R, "wheel-rr"),
  );
  for (const z of [AXLE_F, AXLE_R]) {
    P.push(cyl(0.04, 0.04, 1.18, 8, TRIM, [0, WHEEL_R, z], [0, 0, Math.PI / 2])); // axle, tucked inside the flanks
    for (const s of S) P.push(...arch(s, z));
  }
  P.push(box(1.16, 0.14, 2.2, TRIM, [0, 0.24, 0])); // underbody pan

  // -- Body masses (paint): a short upright two-box tub --------------------------
  P.push(paint(2 * BODY_HALF, WAIST - FLOOR, 2 * NOSE, [0, (FLOOR + WAIST) / 2, 0])); // main tub, sill to waist
  P.push(paint(2 * BODY_HALF - 0.03, 0.06, 2 * NOSE + 0.02, [0, WAIST + 0.02, 0])); // waist cap sealing the tub top
  // Softly rounded front and rear vertical corners (paint cylinders tucked to
  // the body flank so they never poke past the wheels).
  for (const s of S) {
    P.push(paintCyl(0.09, WAIST - FLOOR, [s * (BODY_HALF - 0.09), (FLOOR + WAIST) / 2, NOSE - 0.09]));
    P.push(paintCyl(0.09, WAIST - FLOOR, [s * (BODY_HALF - 0.09), (FLOOR + WAIST) / 2, -(NOSE - 0.09)]));
  }
  // Bonnet panel: a slightly domed paint deck over the short front section, with
  // a rounded leading edge so the nose reads soft, not sharp.
  P.push(paint(1.2, 0.05, 0.55, [0, WAIST + 0.03, 0.98]));
  P.push(paintCyl(0.06, 1.2, [0, WAIST + 0.02, NOSE], [0, 0, Math.PI / 2]));
  // Short boot deck behind the cabin — kept low so the tail reads as a stubby
  // boot, never an open pickup bed.
  P.push(paint(1.22, 0.05, 0.42, [0, WAIST - 0.02, -1.03]));

  // -- Greenhouse: tall upright glass filling the cabin, sealed white roof --------
  // Near-upright screens; a tall side-glass band fills the cabin so the pillars
  // are not stretched. The white roof box drops onto the pillars, its underside
  // overlapping the glass/screen tops so there is NO void between them.
  P.push(box(0.03, 0.03, 1.2, TRIM, [0, WAIST + 0.05, 0.66])); // cowl step at the screen base
  P.push(box(1.06, 0.5, 0.045, SCREEN, [0, 1.047, 0.625], [-0.14, 0, 0])); // near-upright windscreen
  P.push(box(1.02, 0.5, 0.045, GLASS, [0, 1.047, -0.765], [0.14, 0, 0])); // near-upright rear screen
  for (const s of S) {
    P.push(paint(0.07, 0.5, 0.09, [s * 0.535, 1.047, 0.625], [-0.14, 0, 0])); // A-pillar
    P.push(paint(0.08, 0.5, 0.1, [s * 0.535, 1.047, -0.765], [0.14, 0, 0])); // C-pillar
    P.push(box(0.03, 0.48, 1.33, GLASS, [s * 0.58, 1.04, -0.065], [0, 0, s * 0.02])); // tall side glass, fills the cabin
    P.push(box(0.045, 0.48, 0.06, PAINT, [s * 0.58, 1.04, -0.065], [0, 0, s * 0.02], { slot: "paint" })); // B-pillar
    P.push(box(0.018, 0.02, 1.44, CHROME, [s * BODY_HALF, WAIST + 0.02, -0.065])); // chrome belt-line trim
  }
  // White roof cap — a thick slab whose underside (1.25) OVERLAPS the glass and
  // screen tops (~1.28-1.30) by ~0.03, so the roof physically caps the cabin
  // with no floating gap. Its sides are flush with the glass (a shallow gutter,
  // no overhanging eave); front and rear edges are rounded.
  P.push(box(2 * ROOF_HALF, 0.1, 1.34, ROOF, [0, 1.3, -0.07])); // roof panel, underside overlapping the glass
  P.push(cyl(0.045, 0.045, 2 * ROOF_HALF, 12, ROOF, [0, 1.31, 0.6], [0, 0, Math.PI / 2])); // rounded front edge
  P.push(cyl(0.045, 0.045, 2 * ROOF_HALF, 12, ROOF, [0, 1.31, -0.74], [0, 0, Math.PI / 2])); // rounded rear edge

  // -- Front fascia --------------------------------------------------------------
  // Round headlamps set into the wings, a bright chrome oval grille with
  // horizontal bars at the centre, indicators low in the wings.
  for (const s of S) {
    P.push(cyl(0.12, 0.12, 0.03, 18, CHROME, [s * 0.48, 0.64, 1.245], [Math.PI / 2, 0, 0])); // chrome headlamp rim, set into the wing
    P.push(cyl(0.095, 0.095, 0.03, 18, LAMP, [s * 0.48, 0.64, 1.27], [Math.PI / 2, 0, 0])); // headlamp glass
    P.push(cyl(0.045, 0.045, 0.02, 12, "#9fb3c0", [s * 0.48, 0.64, 1.288], [Math.PI / 2, 0, 0])); // reflector pupil
  }
  P.push(box(0.5, 0.3, 0.03, DARK, [0, 0.52, 1.25])); // grille void backing
  P.push(box(0.54, 0.34, 0.02, CHROME, [0, 0.52, 1.24])); // chrome grille surround (proud frame behind)
  for (let i = 0; i < 6; i++) {
    P.push(box(0.46, 0.018, 0.02, BUMPER, [0, 0.39 + i * 0.052, 1.272])); // horizontal chrome bars
  }
  P.push(cyl(0.045, 0.045, 0.02, 12, CHROME, [0, 0.68, 1.28], [Math.PI / 2, 0, 0])); // round bonnet badge
  // Chrome overrider bumper: a slim blade hugging the nose with two bump posts.
  P.push(box(1.2, 0.09, 0.08, CHROME, [0, 0.34, 1.285]));
  for (const s of S) P.push(box(0.07, 0.18, 0.08, CHROME, [s * 0.3, 0.38, 1.29])); // overrider bumps
  P.push(paint(1.24, 0.2, 0.22, [0, 0.28, 1.19])); // valance filling under the bumper to the arches
  P.push(box(0.36, 0.11, 0.02, PLATE, [0, 0.34, 1.325])); // front plate on the bumper

  // -- Rear fascia ---------------------------------------------------------------
  // Small round tail lamps, a short upright boot lid with external hinges, twin
  // exhaust, chrome overrider bumper.
  P.push(paint(1.2, 0.4, 0.05, [0, 0.54, -1.25])); // boot lid on the rear face
  P.push(box(0.02, 0.06, 0.04, CHROME, [0, 0.72, -1.275])); // boot handle
  for (const s of S) {
    P.push(box(0.02, 0.15, 0.04, CHROME, [s * 0.52, 0.54, -1.27])); // external boot hinge blade
    P.push(cyl(0.055, 0.055, 0.03, 14, CHROME, [s * 0.5, 0.46, -1.27], [Math.PI / 2, 0, 0])); // tail-lamp base
    P.push(cyl(0.04, 0.04, 0.04, 14, RED, [s * 0.5, 0.46, -1.29], [Math.PI / 2, 0, 0])); // round red tail lamp
    P.push(cyl(0.04, 0.04, 0.14, 10, "#3f4348", [s * 0.15, 0.22, -1.37], [Math.PI / 2, 0, 0])); // twin exhaust stub
  }
  P.push(box(1.2, 0.09, 0.08, CHROME, [0, 0.34, -1.285])); // rear overrider bumper blade
  for (const s of S) P.push(box(0.07, 0.18, 0.08, CHROME, [s * 0.3, 0.38, -1.29])); // rear overrider bumps
  P.push(paint(1.24, 0.2, 0.22, [0, 0.28, -1.19])); // rear valance under the bumper
  P.push(box(0.36, 0.11, 0.02, PLATE, [0, 0.34, -1.325])); // rear plate

  // -- Furniture -----------------------------------------------------------------
  for (const s of S) {
    // External door hinges (the Mk1 signature) — two small proud chrome barrel
    // tabs at the door's front shutline, reading as a hinge line.
    P.push(box(0.03, 0.03, 0.07, CHROME, [s * (BODY_HALF + 0.008), 0.66, 0.5]));
    P.push(box(0.03, 0.03, 0.07, CHROME, [s * (BODY_HALF + 0.008), 0.42, 0.5]));
    P.push(box(0.02, 0.025, 0.11, CHROME, [s * (BODY_HALF + 0.008), 0.55, -0.05])); // door pull handle
    // Small round wing mirror on a short stalk, tucked at the belt line.
    P.push(box(0.05, 0.015, 0.015, TRIM, [s * (BODY_HALF + 0.015), 0.76, 0.44]));
    P.push(cyl(0.038, 0.038, 0.02, 12, CHROME, [s * (BODY_HALF + 0.05), 0.77, 0.44], [0, 0, Math.PI / 2]));
    // Bonnet seam: a thin recessed line down the wing (Mk1 external welded seam).
    P.push(box(0.014, 0.03, 0.5, TRIM, [s * 0.5, WAIST + 0.05, 0.98]));
  }
  for (const ind of INDICATORS) {
    P.push(box(ind.size[0], ind.size[1], ind.size[2], AMBER, ind.pos, undefined, { name: ind.name }));
  }

  return P;
}

// One wheel revolution per cycle; positive x-rotation rolls the car toward +Z.
const WHEEL_PIVOTS: ReadonlyArray<{ name: string; pivot: Vec3 }> = [
  { name: "wheel-fl", pivot: [-WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-fr", pivot: [WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-rl", pivot: [-WHEEL_X, WHEEL_R, AXLE_R] },
  { name: "wheel-rr", pivot: [WHEEL_X, WHEEL_R, AXLE_R] },
];

export const miniCooperClassic = defineModel({
  id: "mini-cooper-classic",
  name: "Mini Cooper (Classic)",
  batchId: "cars",
  tags: ["car", "mini", "cooper", "classic", "citycar", "compact"],
  summary: "A tiny classic Mk1 Mini Cooper at true scale, in iconic red with a white roof.",
  description:
    "A classic Mk1 Mini Cooper (~1965) at real 1 unit = 1 metre scale (about " +
    "2.9-3.05 long — TINY — 1.41 wide, 1.35 tall, ~2.0 wheelbase, on 10-inch " +
    "steel wheels with chrome hubcaps pushed to the extreme corners with almost " +
    "no overhang: the Mini's 'a wheel at each corner'). A short, stubby, upright " +
    "two-box: short bonnet, a tall upright greenhouse whose glass fills the " +
    "cabin, and a short tail. The iconic two-tone paint — red lower body with a " +
    "contrasting white roof sitting flush on the cabin — round headlamps in the " +
    "wings, a bright chrome oval grille with horizontal bars, chrome overrider " +
    "bumpers, external door hinges, a bonnet seam, round wing mirrors, small " +
    "round tail lamps and a twin exhaust. Body panels carry the 'paint' recolour " +
    "slot (iconic Mini red) with a white roof left unslotted; `drive` spins the " +
    "wheels and `hazards` blinks the four indicators.",
  author: "Fable 5",
  footprint: 1.6,
  parts,
  animations: [
    {
      id: "drive",
      label: "Drive",
      duration: 1,
      tracks: WHEEL_PIVOTS.map(({ name, pivot }) => ({
        target: name,
        pivot,
        rotation: [
          { t: 0, value: [0, 0, 0] as Vec3 },
          { t: 0.5, value: [Math.PI, 0, 0] as Vec3 },
          { t: 1, value: [2 * Math.PI, 0, 0] as Vec3 },
        ],
      })),
    },
    {
      id: "hazards",
      label: "Hazard lights",
      duration: 1,
      tracks: INDICATORS.map(({ name, pos }) => ({
        target: name,
        pivot: pos,
        scale: [
          { t: 0, value: 1 },
          { t: 0.42, value: 1 },
          { t: 0.5, value: 0.02 },
          { t: 0.92, value: 0.02 },
          { t: 1, value: 1 },
        ],
      })),
    },
  ],
});
